import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "@/models/responses/auth/user.response";
import { clearOutstandingPenaltyNoticeDismissals } from "@/utils/outstandingPenaltyNoticeStorage";

/** Pre-tourCompleted localStorage key; safe to remove on load (no longer read). */
const LEGACY_PCC_TOUR_STORAGE_KEY = "has_seen_pcc_tour";

interface AuthState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentEmployer: any | null;
  isAuthenticated: boolean;
  isIntentionalLogout: boolean;
  /**
   * In-memory only (not persisted): after "Take Tour", hide welcome if user
   * leaves the dashboard before finishing. Reset on login / logout.
   */
  tourWelcomeDismissedThisSession: boolean;
  /**
   * In-memory only (not persisted): tracks whether the anti-bribery
   * important notice has already been shown this session, so it appears
   * once per login (after the tour + outstanding-penalty notice) rather
   * than on every Dashboard render/navigation. Reset on login / logout so
   * it reappears on the next login.
   */
  importantNoticeShownThisSession: boolean;
}

interface AuthActions {
  setCurrentEmployer: (employer: User) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  login: (employer: any) => void;
  /**
   * Clears the auth state.
   * @param intentional Pass `true` (default) when the user explicitly logged
   * out (e.g. clicking the Logout button) and `false` when the logout was
   * forced by the system (e.g. expired/invalid session). When `false`,
   * `AuthListener` will surface the auth-expired toast.
   */
  logout: (intentional?: boolean) => void;
  /** After PATCH tour-completed succeeds; keeps UI in sync without refetch. */
  markEmployerTourCompleted: () => void;
  dismissPccTourWelcomeForSession: () => void;
  markImportantNoticeShownForSession: () => void;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      // Initial state
      currentEmployer: null,
      isAuthenticated: false,
      isIntentionalLogout: false,
      tourWelcomeDismissedThisSession: false,
      importantNoticeShownThisSession: false,

      // Actions
      setCurrentEmployer: (employer) => set({ currentEmployer: employer }),
      login: (employer) => {
        console.log("login", employer);
        set({
          currentEmployer: employer,
          isAuthenticated: true,
          isIntentionalLogout: false,
          tourWelcomeDismissedThisSession: false,
          importantNoticeShownThisSession: false,
        });
      },
      markEmployerTourCompleted: () =>
        set((state) => ({
          currentEmployer:
            state.currentEmployer != null
              ? { ...state.currentEmployer, tourCompleted: true }
              : null,
        })),
      dismissPccTourWelcomeForSession: () =>
        set({ tourWelcomeDismissedThisSession: true }),
      markImportantNoticeShownForSession: () =>
        set({ importantNoticeShownThisSession: true }),
      logout: (intentional = true) => {
        // Reset the per-employer notice dismissal on every logout, including a
        // forced one (expired session / failed silent refresh). A logout ends
        // the session, so the next login should surface the outstanding-penalty
        // notice again if penalties remain.
        clearOutstandingPenaltyNoticeDismissals();
        set({
          currentEmployer: null,
          isAuthenticated: false,
          isIntentionalLogout: intentional,
          tourWelcomeDismissedThisSession: false,
          importantNoticeShownThisSession: false,
        });
      },
    }),
    {
      name: "auth-storage", // unique name for localStorage key
      partialize: (state) => ({
        currentEmployer: state.currentEmployer,
        isAuthenticated: state.isAuthenticated,
        isIntentionalLogout: state.isIntentionalLogout,
      }),
      onRehydrateStorage: () => () => {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(LEGACY_PCC_TOUR_STORAGE_KEY);
        }
      },
    }
  )
);
